const projectId = process.env.REACT_APP_SANITY_PROJECT_ID;
const dataset = process.env.REACT_APP_SANITY_DATASET;
const apiVersion = process.env.REACT_APP_SANITY_API_VERSION || "2025-01-01";
const writeToken = process.env.REACT_APP_SANITY_WRITE_TOKEN;

const getApiUrl = (path, apiRoot = "data") => {
  if (!projectId || !dataset) {
    throw new Error("Sanity project configuration is missing.");
  }

  return `https://${projectId}.api.sanity.io/v${apiVersion}/${apiRoot}/${path}/${dataset}`;
};

const getHeaders = (contentType = "application/json") => ({
  "Content-Type": contentType,
  ...(writeToken ? { Authorization: `Bearer ${writeToken}` } : {}),
});

const uploadAttachment = async (file) => {
  if (!file || !file.size) return null;

  if (file.size > 3 * 1024 * 1024) {
    throw new Error("The attachment must be smaller than 3MB.");
  }

  const response = await fetch(getApiUrl("files", "assets"), {
    method: "POST",
    headers: {
      ...getHeaders(file.type || "application/octet-stream"),
      "Content-Disposition": `attachment; filename="${encodeURIComponent(file.name)}"`,
    },
    body: file,
  });

  if (!response.ok) {
    let detail = "The attachment could not be uploaded.";
    try {
      const error = await response.json();
      detail = error.error?.description || error.message || detail;
    } catch {
      // Keep the user-facing message useful if Sanity does not return JSON.
    }
    throw new Error(detail);
  }
  const result = await response.json();
  return {
    _type: "file",
    asset: { _type: "reference", _ref: result.document._id },
  };
};

export const submitContactForm = async (values) => {
  const missingSettings = [
    !projectId && "REACT_APP_SANITY_PROJECT_ID",
    !dataset && "REACT_APP_SANITY_DATASET",
    !writeToken && "REACT_APP_SANITY_WRITE_TOKEN",
  ].filter(Boolean);

  if (missingSettings.length) {
    throw new Error(`Sanity contact form configuration is incomplete. Missing: ${missingSettings.join(", ")}.`);
  }

  const attachment = await uploadAttachment(values.attachment);
  const submissionTypes = {
    "Request Quote": "quoteRequest",
    Job: "jobApplication",
    "General Enquiry": "generalEnquiry",
  };
  const document = {
    _type: submissionTypes[values.enquiryType] || "generalEnquiry",
    submittedAt: new Date().toISOString(),
    company: values.company,
    companyType: values.companyType,
    street: values.street,
    postCode: values.postCode,
    city: values.city,
    state: values.state,
    country: values.country,
    website: values.website,
    salutation: values.salutation,
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone,
    department: values.department,
    position: values.position,
    request: values.request,
    privacyAccepted: values.privacyAccepted === "on",
    ...(attachment ? { attachment } : {}),
  };

  const response = await fetch(getApiUrl("mutate"), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ mutations: [{ create: document }] }),
  });

  if (!response.ok) {
    let detail = "Your request could not be sent.";
    try {
      const error = await response.json();
      detail = error.error?.description || error.message || detail;
    } catch {
      // Keep the user-facing message useful if Sanity does not return JSON.
    }
    throw new Error(detail);
  }
  return response.json();
};
